import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { layoutFleetPerspectivePanels, layoutFleetPerspectiveStacks } from "../utils/fleetTimelinePerspective.js";

const DESKTOP_QUERY = "(min-width: 1120px) and (pointer: fine)";
const GESTURE_THRESHOLD = 12;

export default function useFleetTimelinePerspective({ scopeKey, enabled = true } = {}) {
  const [available, setAvailable] = useState(() => typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches);
  const [activeScope, setActiveScope] = useState(null);
  const [viewport, setViewport] = useState(null);
  const finishGestureRef = useRef(null);
  const isInclined = available && activeScope === scopeKey;
  const inclinedRef = useRef(isInclined);
  inclinedRef.current = isInclined;

  const selectView = useCallback((inclined) => {
    if (!available || inclinedRef.current === inclined) return;
    inclinedRef.current = inclined;
    setActiveScope(inclined ? scopeKey : null);
  }, [available, scopeKey]);
  const toggle = useCallback(() => selectView(!inclinedRef.current), [selectView]);
  const onKeyDown = useCallback((event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (!["ArrowUp", "ArrowDown", "Home"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    selectView(event.key === "ArrowUp");
  }, [selectView]);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const change = () => {
      setAvailable(media.matches);
      if (!media.matches) setActiveScope(null);
    };
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);
  useEffect(() => setActiveScope(null), [scopeKey]);

  useLayoutEffect(() => {
    if (!viewport || !isInclined || !enabled) return undefined;
    let frame = null;
    const layout = () => {
      frame = null;
      // Work only on content/width changes. Moving the mouse never lays out the Gantt.
      const layouts = [...viewport.querySelectorAll('[data-fleet-timeline-track]')].map((track) => {
        track.style.setProperty('--fleet-track-width', `${track.clientWidth}px`);
        const trackRect = track.getBoundingClientRect();
        const stacks = [...track.querySelectorAll('.fleet-perspective-stack')].map((node) => {
          const panels = node.querySelector('.fleet-perspective-panels');
          const panelLayout = layoutFleetPerspectivePanels([...panels.querySelectorAll('.fleet-perspective-panel, .fleet-perspective-ribbon')].map((panel) => ({
            node: panel, width: panel.offsetWidth, height: panel.offsetHeight,
            rowKey: panel.dataset.fleetPerspectiveRow,
          })), track.clientWidth);
          return { ...panelLayout, node, container: panels,
            anchorX: node.getBoundingClientRect().left - trackRect.left - track.clientLeft };
        });
        return { row: track.closest('[data-fleet-timeline-row]'),
          stacks: layoutFleetPerspectiveStacks(stacks, track.clientWidth) };
      });
      for (const { row, stacks } of layouts) {
        const height = Math.max(0, ...stacks.map((stack) => stack.height + stack.lift));
        row.style.setProperty('--fleet-stack-clearance', `${Math.max(0, height - 10)}px`);
        for (const stack of stacks) {
          stack.container.style.width = `${stack.width}px`;
          stack.container.style.height = `${stack.height}px`;
          for (const panel of stack.panels) {
            panel.node.style.left = `${panel.x}px`;
            panel.node.style.top = `${panel.y}px`;
          }
          const shift = stack.x + stack.width / 2 - stack.anchorX;
          const supportShift = stack.x + stack.anchorOffset - stack.anchorX;
          stack.node.style.setProperty('--fleet-stack-shift', `${shift}px`);
          stack.node.style.setProperty('--fleet-stack-lift', `${stack.lift}px`);
          stack.node.style.setProperty('--fleet-support-length', `${Math.hypot(4 + stack.lift, supportShift)}px`);
          stack.node.style.setProperty('--fleet-support-angle', `${Math.atan2(supportShift, 4 + stack.lift) * 180 / Math.PI}deg`);
        }
      }
    };
    const schedule = () => { if (frame === null) frame = window.requestAnimationFrame(layout); };
    const widths = new WeakMap();
    const resize = new ResizeObserver((entries) => {
      for (const { target, contentRect } of entries) {
        if (widths.get(target) !== contentRect.width) {
          widths.set(target, contentRect.width);
          schedule();
        }
      }
    });
    resize.observe(viewport);
    resize.observe(viewport.firstElementChild);
    const rows = viewport.querySelector('[data-fleet-timeline-rows-canvas]');
    if (rows) resize.observe(rows);
    const mutations = new MutationObserver((entries) => {
      if (entries.some((entry) => entry.type !== 'attributes'
        || entry.target.matches('.fleet-timeline-segment, [data-independent-dispatch-period-columns]'))) schedule();
    });
    mutations.observe(viewport, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
    layout();
    return () => {
      resize.disconnect();
      mutations.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      for (const row of viewport.querySelectorAll('[data-fleet-timeline-row]')) row.style.removeProperty('--fleet-stack-clearance');
      for (const track of viewport.querySelectorAll('[data-fleet-timeline-track]')) track.style.removeProperty('--fleet-track-width');
    };
  }, [enabled, isInclined, viewport]);

  useEffect(() => {
    if (!viewport || !available || !enabled) return undefined;
    const preventMiddle = (event) => { if (event.button === 1) { event.preventDefault(); event.stopPropagation(); } };
    const down = (event) => {
      if (event.button !== 1 || event.pointerType === "touch") return;
      event.preventDefault();
      event.stopPropagation();
      finishGestureRef.current?.();
      const pointerId = event.pointerId;
      let lastY = event.clientY;
      let distance = 0;
      viewport.setPointerCapture?.(pointerId);
      viewport.dataset.viewGesture = "true";
      const move = (moveEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        if (!(moveEvent.buttons & 4)) { finish(); return; }
        moveEvent.preventDefault();
        const delta = moveEvent.clientY - lastY;
        lastY = moveEvent.clientY;
        if (!delta) return;
        distance = Math.sign(delta) === Math.sign(distance) ? distance + delta : delta;
        if (Math.abs(distance) >= GESTURE_THRESHOLD) {
          selectView(distance < 0);
          distance = 0;
        }
      };
      const finish = () => {
        window.removeEventListener("pointermove", move, true);
        window.removeEventListener("pointerup", up, true);
        window.removeEventListener("pointercancel", up, true);
        window.removeEventListener("blur", finish);
        window.removeEventListener("keydown", keyDown, true);
        document.removeEventListener("visibilitychange", visibility);
        viewport.removeEventListener("lostpointercapture", finish);
        delete viewport.dataset.viewGesture;
        if (viewport.hasPointerCapture?.(pointerId)) viewport.releasePointerCapture(pointerId);
        finishGestureRef.current = null;
      };
      const up = (upEvent) => { if (upEvent.pointerId === pointerId) finish(); };
      const visibility = () => { if (document.hidden) finish(); };
      const keyDown = (keyEvent) => { if (keyEvent.key === 'Escape') finish(); };
      finishGestureRef.current = finish;
      window.addEventListener("pointermove", move, { capture: true, passive: false });
      window.addEventListener("pointerup", up, true);
      window.addEventListener("pointercancel", up, true);
      window.addEventListener("blur", finish);
      window.addEventListener("keydown", keyDown, true);
      document.addEventListener("visibilitychange", visibility);
      viewport.addEventListener("lostpointercapture", finish);
    };
    viewport.addEventListener("pointerdown", down, true);
    viewport.addEventListener("mousedown", preventMiddle, true);
    viewport.addEventListener("auxclick", preventMiddle, true);
    return () => {
      finishGestureRef.current?.();
      viewport.removeEventListener("pointerdown", down, true);
      viewport.removeEventListener("mousedown", preventMiddle, true);
      viewport.removeEventListener("auxclick", preventMiddle, true);
    };
  }, [available, enabled, selectView, viewport]);

  const blockDrag = (event) => {
    if (!isInclined) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
  };
  return { available, isInclined, toggle, onKeyDown, viewportRef: setViewport, blockDrag };
}
